import React, { useState, useEffect, useRef } from "react"
import styles from "./ListBox.module.css"

export interface ListBoxProps {
    data: { fieldName: string; value: string[] }
    onValueChange: (fieldName: string, valueArray: string[]) => void
    canEdit: boolean
}

const ListBox: React.FC<ListBoxProps> = ({ data, onValueChange, canEdit }) => {
    const [values, setValues] = useState<string[]>((data.value as string[]) ?? [])
    const [editingIndex, setEditingIndex] = useState<number | null>(null)
    const [editingValue, setEditingValue] = useState<string>("")
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        setValues((data.value as string[]) ?? [])
    }, [data.value])

    useEffect(() => {
        if (editingIndex !== null) {
            inputRef.current?.focus()
        }
    }, [editingIndex])

    const handleAddItem = () => {
        const newValues = [...values, ""]
        setValues(newValues)
        setEditingIndex(newValues.length - 1)
        setEditingValue("")
    }

    const handleItemChange = (newValue: string) => {
        setEditingValue(newValue)
    }

    const handleBlur = () => {
        if (editingIndex !== null) {
            if (editingValue.trim() === "") {
                // Remove the item if the input is empty
                const newValues = values.filter((_, index) => index !== editingIndex)
                if (JSON.stringify(newValues) !== JSON.stringify(data.value)) {
                    onValueChange(data.fieldName, newValues)
                }
                setValues(newValues)
            } else {
                // Update the item if the input is not empty
                const newValues = values.map((item, index) => (index === editingIndex ? editingValue : item))
                if (JSON.stringify(newValues) !== JSON.stringify(data.value)) {
                    onValueChange(data.fieldName, newValues)
                }
                setValues(newValues)
            }
            setEditingIndex(null)
            setEditingValue("")
        }
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter" || event.key === "Escape") {
            handleBlur()
        }
    }

    console.log("Values = ", values)

    const listItems = values?.map((item, index) => (
        <div key={`${data.fieldName}-${index}`} className={styles.listItem}>
            {editingIndex === index ? (
                <input ref={inputRef} value={editingValue} onChange={(e) => handleItemChange(e.target.value)} onBlur={handleBlur} onKeyDown={handleKeyDown} className={styles.input} />
            ) : (
                <div
                    onClick={() => {
                        if (canEdit) {
                            setEditingIndex(index)
                            setEditingValue(item)
                        }
                    }}
                    className={styles.itemText}
                >
                    {item}
                </div>
            )}
        </div>
    ))

    const addItemButton = (
        <div key={`add-item`} className={`${styles.listItem} ${styles.addItemButton}`} onClick={handleAddItem}>
            + Add Item
        </div>
    )

    return (
        <div className={styles.list}>
            {listItems}
            {canEdit && addItemButton}
        </div>
    )
}

export default ListBox
